import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useRef } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../lib/firebase";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { formatMoney, localized } from "../lib/format";
import { HallFormDialog, MenuFormDialog, type EditableHall } from "../components/HallFormDialog";
import { HallPhotosDialog } from "../components/HallPhotosDialog";

interface PriceSuggestion {
  occupancyPct: number;
  peakWeekday: string | null;
  rationale: string;
  suggestions: {
    menuId: string;
    menuName: Record<string, string>;
    currentPerGuestMinor: number;
    suggestedPerGuestMinor: number;
    adjustPct: number;
  }[];
}

interface HallDoc {
  id: string;
  name: Record<string, string>;
  status: string;
  cityId: string;
  hallType: string;
  capacity?: { total: number; mens?: number; womens?: number };
  priceRange?: { minPerGuestMinor: number; currency: string };
  rating?: { avg: number; count: number };
  coverUrl?: string;
  galleryCount?: number;
  hallFeeMinor?: number;
  depositPct?: number;
  instantBooking?: boolean;
  amenities?: string[];
}

const STATUS_LABEL: Record<string, { label: string; color: "default" | "success" | "warning" }> = {
  draft: { label: "پیش‌نویس", color: "default" },
  pending_review: { label: "در انتظار بررسی", color: "warning" },
  published: { label: "منتشر شده", color: "success" },
  suspended: { label: "معلق", color: "default" },
};

export function HallsPage() {
  const { orgId } = useSession();
  const [halls, setHalls] = useState<HallDoc[]>([]);
  const [suggestion, setSuggestion] = useState<{ hallName: string; data: PriceSuggestion } | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editHall, setEditHall] = useState<EditableHall | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [photosFor, setPhotosFor] = useState<HallDoc | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadTargetRef = useRef<string | null>(null);

  function pickPhoto(hallId: string) {
    uploadTargetRef.current = hallId;
    fileInputRef.current?.click();
  }

  async function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const hallId = uploadTargetRef.current;
    e.target.value = "";
    if (!file || !hallId || !orgId) return;
    if (file.size > 15 * 1024 * 1024) {
      setToast("عکس باید کمتر از ۱۵ مگابایت باشد");
      return;
    }
    setUploadingId(hallId);
    try {
      // Org-scoped path so Storage rules can verify tenancy from the token
      // claim (Storage rules can't read Firestore).
      const path = `orgs/${orgId}/halls/${hallId}/${Date.now()}_${file.name.replace(/[^\w.-]/g, "_")}`;
      const dest = storageRef(storage, path);
      await uploadBytes(dest, file, { contentType: file.type });
      const url = await getDownloadURL(dest);
      await api(`/orgs/${orgId}/halls/${hallId}/media`, {
        method: "POST",
        body: { url, storagePath: path },
      });
      setToast("عکس اضافه شد");
    } catch (err) {
      setToast(err instanceof Error ? err.message : "خطا در آپلود عکس");
    } finally {
      setUploadingId(null);
    }
  }

  async function publish(hallId: string) {
    try {
      await api(`/orgs/${orgId}/halls/${hallId}/publish`, { method: "POST" });
      setToast("برای بررسی و انتشار فرستاده شد");
    } catch (e) {
      setToast(e instanceof Error ? e.message : "خطا");
    }
  }

  async function loadSuggestion(hall: HallDoc) {
    try {
      const data = await api<PriceSuggestion>(
        `/orgs/${orgId}/ai/price-suggestions?hallId=${hall.id}`,
      );
      setSuggestion({ hallName: localized(hall.name), data });
    } catch {
      setSuggestion(null);
    }
  }

  useEffect(() => {
    if (!orgId) return;
    const q = query(collection(db, "halls"), where("orgId", "==", orgId));
    return onSnapshot(q, (snap) => {
      setHalls(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<HallDoc, "id">) })));
    });
  }, [orgId]);

  return (
    <>
      <Stack direction="row" alignItems="center" mb={2} spacing={2}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          تالارهای من
        </Typography>
        <Button variant="contained" onClick={() => setFormOpen(true)}>
          + ثبت تالار
        </Button>
      </Stack>
      <Grid container spacing={2}>
        {halls.map((hall) => {
          const status = STATUS_LABEL[hall.status] ?? { label: hall.status, color: "default" as const };
          return (
            <Grid item xs={12} sm={6} md={4} key={hall.id}>
              <Card>
                {hall.coverUrl && (
                  <CardMedia component="img" height="160" image={hall.coverUrl} />
                )}
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{localized(hall.name)}</Typography>
                    <Chip size="small" label={status.label} color={status.color} />
                  </Stack>
                  <Typography color="text.secondary" variant="body2" mt={0.5}>
                    ظرفیت {hall.capacity?.total ?? "—"} نفر
                    {hall.priceRange
                      ? ` · فی نفر از ${formatMoney(hall.priceRange.minPerGuestMinor, hall.priceRange.currency)}`
                      : ""}
                  </Typography>
                  {hall.rating && hall.rating.count > 0 && (
                    <Typography variant="body2" color="secondary.main" mt={0.5}>
                      ★ {hall.rating.avg} ({hall.rating.count} نظر)
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                    <Button
                      size="small"
                      onClick={() =>
                        setEditHall({
                          id: hall.id,
                          name: hall.name,
                          cityId: hall.cityId,
                          hallType: hall.hallType,
                          capacity: hall.capacity,
                          hallFeeMinor: hall.hallFeeMinor,
                          depositPct: hall.depositPct,
                          instantBooking: hall.instantBooking,
                          amenities: hall.amenities,
                        })
                      }
                    >
                      ✏️ ویرایش
                    </Button>
                    <Button
                      size="small"
                      disabled={uploadingId === hall.id}
                      onClick={() => pickPhoto(hall.id)}
                    >
                      {uploadingId === hall.id ? "در حال آپلود…" : "+ عکس"}
                    </Button>
                    <Button size="small" onClick={() => setPhotosFor(hall)}>
                      🖼️ عکس‌ها
                      {hall.galleryCount ? ` (${hall.galleryCount})` : ""}
                    </Button>
                    <Button size="small" onClick={() => setMenuFor(hall.id)}>
                      + منو
                    </Button>
                    {hall.status === "draft" && (
                      <Button size="small" variant="contained" onClick={() => publish(hall.id)}>
                        ارسال برای انتشار
                      </Button>
                    )}
                    <Button size="small" onClick={() => loadSuggestion(hall)}>
                      🧠 پیشنهاد قیمت
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
        {halls.length === 0 && (
          <Grid item xs={12}>
            <Typography color="text.secondary" textAlign="center" py={6}>
              هنوز تالاری ثبت نکرده‌اید — با دکمه «ثبت تالار» شروع کنید.
            </Typography>
          </Grid>
        )}
      </Grid>

      {orgId && (
        <>
          <HallPhotosDialog
            orgId={orgId}
            hallId={photosFor?.id ?? null}
            hallName={photosFor ? localized(photosFor.name) : ""}
            open={photosFor !== null}
            onClose={() => setPhotosFor(null)}
            onUpload={() => photosFor && pickPhoto(photosFor.id)}
            uploading={uploadingId !== null && uploadingId === photosFor?.id}
          />
          <HallFormDialog
            orgId={orgId}
            open={formOpen}
            onClose={() => setFormOpen(false)}
            onSaved={(hallId) => {
              setToast("تالار ثبت شد — حالا منوهایش را اضافه کنید و بعد «ارسال برای انتشار» را بزنید");
              setMenuFor(hallId);
            }}
          />
          <HallFormDialog
            orgId={orgId}
            open={!!editHall}
            hall={editHall}
            onClose={() => setEditHall(null)}
            onSaved={() => setToast("تغییرات تالار ذخیره شد")}
          />
          <MenuFormDialog
            orgId={orgId}
            hallId={menuFor}
            onClose={() => setMenuFor(null)}
            onCreated={() => setToast("منو ثبت شد")}
          />
        </>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={onFileChosen}
      />
      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert onClose={() => setToast(null)} severity="info">
          {toast}
        </Alert>
      </Snackbar>

      <Dialog open={!!suggestion} onClose={() => setSuggestion(null)} maxWidth="sm" fullWidth>
        {suggestion && (
          <>
            <DialogTitle>پیشنهاد قیمت — {suggestion.hallName}</DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <Alert severity="info">
                  اشغال ۹۰ روز آینده: {suggestion.data.occupancyPct}٪
                  {suggestion.data.peakWeekday && ` · پرتقاضاترین روز: ${suggestion.data.peakWeekday}`}
                  <br />
                  {suggestion.data.rationale}
                </Alert>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>منو</TableCell>
                      <TableCell>قیمت فعلی (فی نفر)</TableCell>
                      <TableCell>پیشنهاد</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suggestion.data.suggestions.map((s) => (
                      <TableRow key={s.menuId}>
                        <TableCell>{localized(s.menuName)}</TableCell>
                        <TableCell>{formatMoney(s.currentPerGuestMinor)}</TableCell>
                        <TableCell>
                          {formatMoney(s.suggestedPerGuestMinor)}
                          {s.adjustPct !== 0 && (
                            <Chip
                              size="small"
                              sx={{ mr: 1 }}
                              color={s.adjustPct > 0 ? "success" : "warning"}
                              label={`${s.adjustPct > 0 ? "+" : ""}${s.adjustPct}٪`}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Typography variant="caption" color="text.secondary">
                  پیشنهادها مشورتی‌اند — تغییر قیمت همیشه با تأیید شماست.
                </Typography>
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
}
